// Pure logic for Add Journey's per-place details: what can be filled in for each
// place before the journey exists (the place card's sections), how
// it is validated, and the requests it becomes once the journey has been
// created. No React and no I/O. Every rule here mirrors the screen that normally
// records the same thing — the server validates all of it again.
//
// Dependencies the server imposes: Book Distribution, Public Response and Media
// belong to a RECORDED CITY VISIT (city_visit_id). They can be filled in all the
// same: a place with any of them but no Visit Info gets its visit recorded
// automatically first, on the place's visit date (autoVisitBody). Accommodation
// and Advice only need the journey and the place.
import { ADVICE_SECTIONS, ADVICE_SECTION_MAX, ADVICE_SECTION_MIN } from "../../constants/tspAdvice";
import { validateVisitFields } from "../city-visits/cityVisitsModel";
import { validateMediaUpload } from "../media-gallery/mediaModel";

export const CITY_TABS = Object.freeze({
  visit: "Visit Info",
  books: "Book Distribution",
  accommodation: "Accommodation",
  mediaAdvice: "Media and Advice",
  response: "Public Response",
});

export const EMPTY_VISIT = Object.freeze({ enabled: false, date: "", time: "", areasVisited: "", latitude: "", longitude: "", remarks: "" });
export const EMPTY_ACCOMMODATION = Object.freeze({ stay_name: "", contact: "", address: "", charges: "", capacity: "", remarks: "" });
export const EMPTY_ADVICE = Object.freeze(Object.fromEntries(ADVICE_SECTIONS.map((section) => [section.key, ""])));
export const EMPTY_RESPONSE = Object.freeze({ rating: "", remarks: "" });

// One stop's details. books: staged rows of BookDistributionRecordsCard;
// accommodations: staged stays; media: [{ id, file, caption }].
export const emptyCityDetails = () => ({
  visit: { ...EMPTY_VISIT },
  books: [],
  accommodations: [],
  media: [],
  advice: { ...EMPTY_ADVICE },
  response: { ...EMPTY_RESPONSE },
});

const ACCOMMODATION_LIMITS = { stay_name: 150, contact: 100, address: 500, remarks: 2000 };
const RESPONSE_REMARKS_MAX = 2000;
const MONEY = /^\d{1,10}(\.\d{1,2})?$/;
const COUNT = /^\d{1,9}$/;

export const adviceWritten = (advice) => ADVICE_SECTIONS.some((section) => (advice[section.key] || "").trim() !== "");

// Anything entered for this stop beyond the route itself?
export function hasCityDetails(details) {
  if (!details) return false;
  return details.visit.enabled || details.books.length > 0 || details.accommodations.length > 0 || details.media.length > 0 || adviceWritten(details.advice) || Boolean(details.response.rating);
}

// Short labels for the chips on a stop row: what will be saved with it.
export function cityDetailsSummary(details) {
  if (!details) return [];
  const chips = [];
  if (details.visit.enabled) chips.push("Visit");
  const books = details.books.reduce((sum, row) => sum + Number(row.qty || 0), 0);
  if (books > 0) chips.push(`${books} books`);
  if (details.accommodations.length) chips.push(`${details.accommodations.length} stay${details.accommodations.length === 1 ? "" : "s"}`);
  if (details.media.length) chips.push(`${details.media.length} media`);
  if (adviceWritten(details.advice)) chips.push("Advice");
  if (details.response.rating) chips.push("Response");
  return chips;
}

// One staged stay -> its field errors ({} when fine).
export function validateAccommodation(draft) {
  const errors = {};
  const name = draft.stay_name.trim();
  if (!name) errors.stay_name = "Stay name is required.";
  Object.entries(ACCOMMODATION_LIMITS).forEach(([key, max]) => {
    if (!errors[key] && (draft[key] || "").trim().length > max) errors[key] = `Must be ${max} characters or fewer.`;
  });
  const charges = String(draft.charges ?? "").trim();
  if (charges && !MONEY.test(charges)) errors.charges = "Charges must be an amount like 500 or 500.50.";
  const capacity = String(draft.capacity ?? "").trim();
  if (capacity && !COUNT.test(capacity)) errors.capacity = "Capacity must be a whole number.";
  return errors;
}

export function validateAdviceSections(advice) {
  for (const section of ADVICE_SECTIONS) {
    const text = (advice[section.key] || "").trim();
    if (text.length > ADVICE_SECTION_MAX) return `${section.label} must be ${ADVICE_SECTION_MAX} characters or fewer.`;
  }
  return null;
}

// Every problem with one stop's details, per tab: { [CITY_TABS.x]: message }.
// `range` = the journey's { startDate, endDate } (visit dates must be inside it).
// Only what was entered is checked; nothing in the details is required.
export function validateCityDetails(details, range) {
  const errors = {};
  if (!details) return errors;
  const { visit } = details;

  if (visit.enabled) {
    if (!range.startDate || !range.endDate) errors[CITY_TABS.visit] = "Set the journey's start and end dates first: the visit date must fall between them.";
    else {
      const checked = validateVisitFields(visit, range);
      if (checked.error) errors[CITY_TABS.visit] = checked.error;
    }
  }

  // Books, public response and media do not need Visit Info: without it the
  // place's visit is recorded automatically when the journey is saved.
  if (details.response.remarks.trim().length > RESPONSE_REMARKS_MAX) errors[CITY_TABS.response] = `Remarks must be ${RESPONSE_REMARKS_MAX} characters or fewer.`;
  else if (details.response.remarks.trim() && !details.response.rating) errors[CITY_TABS.response] = "Choose a rating for the public response, or clear its remarks.";

  {
    const badMedia = details.media.find((item) => Object.keys(validateMediaUpload({ caption: item.caption, journeyId: "x", cityVisitId: "x" }, item.file)).length > 0);
    if (badMedia) {
      const found = validateMediaUpload({ caption: badMedia.caption, journeyId: "x", cityVisitId: "x" }, badMedia.file);
      errors[CITY_TABS.mediaAdvice] = `${badMedia.file?.name || "A file"}: ${found.file || found.caption}`;
    }
  }
  if (!errors[CITY_TABS.mediaAdvice]) {
    const adviceError = validateAdviceSections(details.advice);
    if (adviceError) errors[CITY_TABS.mediaAdvice] = adviceError;
  }

  const badStay = details.accommodations.find((stay) => Object.keys(validateAccommodation(stay)).length > 0);
  if (badStay) errors[CITY_TABS.accommodation] = `${badStay.stay_name || "A stay"}: ${Object.values(validateAccommodation(badStay))[0]}`;
  return errors;
}

// ------------------------------------------------------------ request bodies

// The visit recorded automatically for a place that has book distribution,
// media or a public response but no Visit Info: just the journey, the place and
// its visit date (time and location are optional on the server and can be added
// later in Visit Info).
export const autoVisitBody = (journeyId, locationId, visitDate) => ({ journey_id: Number(journeyId), location_id: Number(locationId), visit_date: visitDate });

// Does this place have something that must be filed under a visit?
export const needsVisitRecord = (details) => details.books.length > 0 || details.media.length > 0 || Boolean(details.response.rating);

export function visitBody(visit, journeyId, locationId, range) {
  const checked = validateVisitFields(visit, range);
  if (checked.error) throw new Error(checked.error);
  const body = { journey_id: Number(journeyId), location_id: Number(locationId), ...checked.fields };
  if (visit.remarks.trim()) body.remarks = visit.remarks.trim();
  return body;
}

// The same batch the Add Book Distribution screen sends.
export const bookBatchBody = (books, cityVisitId) => ({
  city_visit_id: Number(cityVisitId),
  entries: books.map((row) => ({
    ...(row.book_set_id ? { book_set_id: row.book_set_id } : { custom_book_name: row.custom_book_name }),
    language: row.language,
    quantity: row.qty,
    distribution_method: row.method,
  })),
});

export function responseBody(response, journeyId, cityVisitId) {
  const body = { journey_id: Number(journeyId), city_visit_id: Number(cityVisitId), response_rating: response.rating };
  if (response.remarks.trim()) body.remarks = response.remarks.trim();
  return body;
}

export function accommodationBody(stay, journeyId, locationId) {
  const body = { location_id: Number(locationId), journey_id: Number(journeyId), stay_name: stay.stay_name.trim() };
  ["contact", "address", "remarks"].forEach((key) => {
    if ((stay[key] || "").trim()) body[key] = stay[key].trim();
  });
  if (String(stay.charges ?? "").trim()) body.charges = Number(stay.charges);
  if (String(stay.capacity ?? "").trim()) body.capacity = Number(stay.capacity);
  return body;
}

export function adviceBody(advice, journeyId, locationId) {
  const body = { journey_id: Number(journeyId), location_id: Number(locationId) };
  ADVICE_SECTIONS.forEach((section) => {
    const text = (advice[section.key] || "").trim();
    if (text) body[section.key] = text;
  });
  return body;
}

// ------------------------------------------------------------ save plan

// The steps that save one stop's details, in the order they must run. A step
// with `needsVisit` waits for (and uses) the stop's newly recorded visit — the
// one from Visit Info, or else one recorded automatically on the place's date.
// Each step: { key, label, needsVisit, run(ctx) } where ctx = { journeyId,
// locationId, cityVisitId, range, plannedDate }. `api` is TspApiService (injected for tests).
export function cityDetailSteps(details, api, { uploadMedia }) {
  const steps = [];
  if (details.visit.enabled) {
    steps.push({ key: "visit", label: "Visit", run: async (ctx) => (await api.createCityVisit(visitBody(details.visit, ctx.journeyId, ctx.locationId, ctx.range))).data });
  } else if (needsVisitRecord(details)) {
    steps.push({
      key: "visit",
      label: "Visit (recorded automatically)",
      run: async (ctx) => (await api.createCityVisit(autoVisitBody(ctx.journeyId, ctx.locationId, ctx.plannedDate || ctx.range.startDate))).data,
    });
  }
  if (details.books.length > 0) {
    const total = details.books.reduce((sum, row) => sum + Number(row.qty || 0), 0);
    steps.push({ key: "books", label: `Book distribution (${total} books)`, needsVisit: true, run: (ctx) => api.createBookDistributionBatch(String(ctx.journeyId), bookBatchBody(details.books, ctx.cityVisitId)) });
  }
  if (details.response.rating) {
    steps.push({ key: "response", label: "Public response", needsVisit: true, run: (ctx) => api.createPublicResponse(responseBody(details.response, ctx.journeyId, ctx.cityVisitId)) });
  }
  details.media.forEach((item) => {
    steps.push({
      key: `media:${item.id}`,
      label: `Media “${item.caption.trim()}”`,
      needsVisit: true,
      run: (ctx) => uploadMedia({ caption: item.caption, journeyId: ctx.journeyId, cityVisitId: ctx.cityVisitId }, item.file),
    });
  });
  details.accommodations.forEach((stay) => {
    steps.push({ key: `stay:${stay.id}`, label: `Accommodation “${stay.stay_name.trim()}”`, run: (ctx) => api.accommodations.create(accommodationBody(stay, ctx.journeyId, ctx.locationId)) });
  });
  if (adviceWritten(details.advice)) {
    steps.push({ key: "advice", label: "Advice", run: (ctx) => api.advice.create(adviceBody(details.advice, ctx.journeyId, ctx.locationId)) });
  }
  return steps;
}

// The created journey's route stop for each form stop. The server stores the
// route in the order it was sent (sequence_number 1..n), so the form's index i
// is sequence i + 1. -> Map(formStopId -> location_id)
export function locationsByStop(formStops, serverRoute = []) {
  const bySequence = new Map(serverRoute.map((stop) => [Number(stop.sequence_number), stop.location_id]));
  return new Map(formStops.map((stop, index) => [stop.id, bySequence.get(index + 1) ?? null]));
}
